import { StandardProps } from "../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant.ts";
import { isInStoreOnly } from "@/utils/variant/isInStoreOnly.ts";
import { isAvailableToPreOrder } from "@/utils/variant/isAvailableToPreOrder.ts";
import { isFutureRelease } from "@/utils/variant/isFutureRelease.ts";
import { ensureIsActive } from "@/utils/product/ensureIsActive.ts";
import { hasAnyStock } from "@/utils/product/hasAnyStock.ts";
import { hasColours } from "@/utils/product/hasColours.ts";
import { Variant, VariantServices } from "@/types/types.ts";
import { DeliveryAndGuaranteesMessageUI } from "./DeliveryAndGuaranteesMessageUI";
import { getReturnsExclusionsMessage } from "@/components/ProductAccordion/components/ProductDeliveryOptionsAccordion/ReturnsExclusions/utils/getReturnsExclusionsMessage.ts";
import { isTwoManDelivery } from "@/utils/product/isTwoManDelivery.ts";
import { isMadeToMeasure } from "@/utils/product/isMadeToMeasure";
import { isPerMetre } from "@/utils/product/isPerMetre";
import { isEligibleForFreeDelivery } from "@/utils/product/isEligibleForFreeDelivery";
import { isEligibleForFreeReturns } from "@/utils/product/isEligibleForFreeReturns";
import {
  BundleServices,
  getBundleServices,
} from "@/utils/product/getBundleServices";
import { DeliveryMessage } from "@/components/DeliveryAndGuaranteesMessage/constants.ts";
import {
  DMS_IDS,
  FREE_CLICK_AND_COLLECT_THRESHOLD,
  FREE_DELIVERY_THRESHOLD,
} from "@/constants/constants.ts";

export function DeliveryAndGuaranteesMessage({
  product,
  configProps,
  flags,
}: Pick<StandardProps, "product" | "configProps" | "flags">) {
  const { variant, isSelected } = useVariant(product);
  const currentPrice = variant.price?.value;
  const isShipFromStore = variant.availability?.shipFromStore === true;

  const returnsStartDateOverride = flags.extendedReturnsPeriodStartDate.value
    ? String(flags.extendedReturnsPeriodStartDate.value)
    : "";
  const returnsEndDateOverride = flags.extendedReturnsPeriodEndDate.value
    ? String(flags.extendedReturnsPeriodEndDate.value)
    : "";

  let freeDeliveryThresholdOverride;

  const freeStandardDeliveryOverThresholdMessage =
    DeliveryMessage.FreeStandardDeliveryOverThreshold;

  const isNewTypographyEnabled = flags?.useNewTypography.value === true;
  const parsed = parseInt(
    flags.freeDeliveryThresholdOverride.value?.toString() ?? "",
    10,
  );
  if (!isNaN(parsed)) {
    freeDeliveryThresholdOverride = parsed;
  }

  const freeDeliveryThreshold =
    freeDeliveryThresholdOverride ?? FREE_DELIVERY_THRESHOLD;

  const services: NonNullable<VariantServices | BundleServices> =
    variant.__typename === "CompositeBundleVariant"
      ? getBundleServices(variant)
      : variant.services || [];

  const guarantees = services.filter(
    ({ __typename }) => __typename === "IncludedGuarantee",
  );

  const eligibleForFreeDelivery = isEligibleForFreeDelivery(product);

  const is2ManDelivery = isTwoManDelivery(product);
  let priceFor2ManDelivery: number | undefined;

  if (is2ManDelivery) {
    const option = product.fulfilmentOptions?.available.find(
      (option) => option.aliases.dmsId === DMS_IDS.TwoPersonDelivery,
    );

    if (option?.price?.value) {
      priceFor2ManDelivery = parseFloat(option.price.value);
    }
  }

  const isM2M = isMadeToMeasure(product);

  const isSellingProductTypeInExclusions = !!getReturnsExclusionsMessage(
    product.browsePageHierarchy,
    product.sellingProductType || "",
  );

  const isBraExclusion =
    isSellingProductTypeInExclusions && product.sellingProductType === "Bras";

  const isOutOfStockSingleSku =
    ensureIsActive(product) &&
    !hasAnyStock(product) &&
    product.variants?.length === 1;

  const isVariantAvailable =
    isInStoreOnly(variant) ||
    isAvailableToPreOrder(variant) ||
    isFutureRelease(variant);

  const hideDeliveryMessage = isOutOfStockSingleSku || isVariantAvailable;

  const isOverFreeDeliveryThreshold =
    parseFloat(currentPrice ?? "0") >= freeDeliveryThreshold;

  let isOverClickAndCollectThreshold =
    parseFloat(currentPrice ?? "0") >= FREE_CLICK_AND_COLLECT_THRESHOLD;

  if (!isSelected) {
    let variants;
    const hasColors = hasColours(product);

    if (hasColors) {
      variants = product.variants.filter(
        (productVariant: Variant) =>
          productVariant.differentiators?.colour ===
          variant.differentiators?.colour,
      );
    } else {
      variants = product.variants;
    }

    isOverClickAndCollectThreshold = !variants.some(
      (variant) =>
        parseFloat(("price" in variant && variant.price.value) || "0") <
        FREE_CLICK_AND_COLLECT_THRESHOLD,
    );
  }

  const isClickAndCollectAvailable =
    !(isShipFromStore && isSelected) &&
    !!product.fulfilmentOptions?.available.some(
      (option) => option.aliases.dmsId === DMS_IDS.ClickAndCollect,
    );

  const getDeliveryMessage = (): DeliveryMessage | string => {
    if (!eligibleForFreeDelivery && !is2ManDelivery) {
      return DeliveryMessage.None;
    }

    if (isM2M) {
      return (
        product.fulfilmentOptions?.available[0]?.headline ||
        DeliveryMessage.None
      );
    }

    if (is2ManDelivery && priceFor2ManDelivery && priceFor2ManDelivery > 0) {
      return DeliveryMessage.None;
    }

    if (is2ManDelivery) {
      return isOverFreeDeliveryThreshold
        ? DeliveryMessage.FreeUKMainlandDelivery
        : DeliveryMessage.FreeUKMainlandDeliveryOverThreshold;
    }

    return isOverFreeDeliveryThreshold
      ? DeliveryMessage.FreeStandardDelivery
      : freeStandardDeliveryOverThresholdMessage.replace(
          "{price}",
          `£${freeDeliveryThreshold}`,
        );
  };

  const message = getDeliveryMessage();

  const messageList: string[] = Array.isArray(configProps?.messageList)
    ? configProps.messageList
    : [];

  const showFreeReturns =
    !is2ManDelivery &&
    !isM2M &&
    !isPerMetre(product) &&
    isEligibleForFreeReturns(product) &&
    flags.hideFreeReturnsMessage.value === false;

  const showFreeReturnsWithRestrictions =
    variant.sellingRestrictions?.age?.bladedArticle ||
    isSellingProductTypeInExclusions;

  if (!configProps?.messageList) {
    return null;
  }

  const deliveryAndGuaranteesPosition =
    flags.deliveryAndGuaranteesPosition.value;

  if (
    configProps?.deliveryAndGuaranteesPosition &&
    configProps.deliveryAndGuaranteesPosition !==
      deliveryAndGuaranteesPosition &&
    configProps.deliveryAndGuaranteesPosition !== undefined
  ) {
    return null;
  }

  const isPositionTwoMatch =
    configProps?.deliveryAndGuaranteesPosition ===
      deliveryAndGuaranteesPosition && deliveryAndGuaranteesPosition === "two";

  const showClickAndCollect =
    eligibleForFreeDelivery && is2ManDelivery
      ? "never"
      : String(configProps?.showClickAndCollect ?? "never");

  return (
    <DeliveryAndGuaranteesMessageUI
      message={message}
      messageList={messageList}
      isClickAndCollectAvailable={isClickAndCollectAvailable}
      showClickAndCollect={showClickAndCollect}
      isOverClickAndCollectThreshold={isOverClickAndCollectThreshold}
      showFreeReturns={showFreeReturns}
      showFreeReturnsWithRestrictions={showFreeReturnsWithRestrictions}
      guarantees={guarantees}
      hideDeliveryMessage={hideDeliveryMessage}
      returnsStartDate={returnsStartDateOverride}
      returnsEndDate={returnsEndDateOverride}
      show2ManDeliveryMessage={is2ManDelivery}
      twoManDeliveryPrice={priceFor2ManDelivery}
      isBraExclusion={isBraExclusion}
      hasHighlightedBackground={isPositionTwoMatch}
      isNewTypographyEnabled={isNewTypographyEnabled}
    />
  );
}
